import React, { useCallback, useContext, useEffect, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { CreateRebateSettings, RebateSettingsDto } from '@/api/Trading-Accounts/RebateSettingsDto'
import { rebateSettingsColumns } from '@/routes/Transactions/rebateSettingsColumns'
import { RebateSettingsForm } from '@/routes/Transactions/RebateSettingsForm'
import { PartnershipsApiClient } from '@/utils/api/partnershipsApi'
import { AuthSessionContext } from '@/utils/AuthContext'
import { hasPermission } from '@/utils/permissionsValidator'
import { SortDescriptor } from '@progress/kendo-data-query'
import { Grid, GridCellProps, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState } from 'features/DataGrid'
import { useGridStatusState } from 'features/DataGrid/hooks/useGridStatusState'
import { isGoBack } from 'lib/react-router-dom'
import { useAppSelector } from 'store'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
})

const useRebateSettingsFetch = () => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [rebateSettings, setRebateSettings] = useState<RebateSettingsDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { tickmillCompanies } = useMapStateToProps()

  useEffect(() => {
    fetchRebateSettingsFetch()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRebateSettingsFetch = async () => {
    await fetchGrid(
      async (gridFilterState: GridFilterActionState): Promise<PageData<RebateSettingsDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSortQuery(gridFilterState.sort)
        const response = await partnershipsApiClient.getRebateSettings({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) {
          setRebateSettings(response.items)
        }
        setTableData({ ...tableData, meta: response })

        return response
      }
    )
  }

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<RebateSettingsDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSortQuery = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  return {
    tickmillCompanies,
    rebateSettings,
    setRebateSettings,
    gridState,
    gridActions,
    tableData,
    setTableData,
    setGridFilters: gridActions.setGridFilters,
    gridStatusState,
    gridStatusActions,
    getQuery,
    getSortQuery,
    fetchRebateSettingsFetch,
  }
}

const useRebateSettingsMutate = () => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const setToast = useContext(ToastContext)

  const handleAdd = async (data: CreateRebateSettings) => {
    await partnershipsApiClient.createRebateSettings(data)
    setToast(successToast(`Rebate Settings added successfully.`))
  }

  const handleEdit = async (id: string, data: CreateRebateSettings) => {
    await partnershipsApiClient.updateRebateSettings(id, data)
    setToast(successToast(`Rebate Settings updated successfully.`))
  }

  const handleRemove = async (data: RebateSettingsDto) => {
    await partnershipsApiClient.deleteRebateSettings(data.id)
    setToast(successToast(`Rebate Settings removed successfully.`))
  }

  return { handleAdd, handleEdit, handleRemove }
}

const useRebateSettingsHandlers = (props: ReturnType<typeof useRebateSettingsFetch>) => {
  const { gridState, gridActions } = props
  const { getQuery, getSortQuery, setRebateSettings, tableData, setTableData } = props

  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await partnershipsApiClient.getRebateSettings({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setRebateSettings(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSortQuery(event.sort)
    const response = await partnershipsApiClient.getRebateSettings({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setRebateSettings(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await partnershipsApiClient.getRebateSettings({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setRebateSettings(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await partnershipsApiClient.getRebateSettings({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults
    gridActions.setGridFilters({
      total,
    })

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setRebateSettings(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return {
    handleSkipCountChange,
    handleGridPageChange,
    handlePageSizeChange,
    handleSortChange,
  }
}

interface ModalProps {
  data: RebateSettingsDto | undefined
  visible: boolean
}

const initialModalState = { data: undefined, visible: false }

const useRebateSettingsModal = () => {
  const [rebateSettingsModal, setRebateSettingsModal] = useState<ModalProps>(initialModalState)

  const handleRebateSettingsModalOpen = (data?: RebateSettingsDto) => {
    setRebateSettingsModal({ data, visible: true })
  }

  const handleRebateSettingsModalClose = () => {
    setRebateSettingsModal(initialModalState)
  }

  return {
    rebateSettingsModal,
    handleRebateSettingsModalOpen,
    handleRebateSettingsModalClose,
  }
}

const useConfirmationModal = () => {
  const [confirmationModal, setConfirmationModal] = useState<ModalProps>(initialModalState)

  const handleConfirmationModalOpen = (data?: RebateSettingsDto) => {
    if (data) {
      setConfirmationModal({ data, visible: true })
    }
  }

  const handleConfirmationModalClose = () => {
    setConfirmationModal(initialModalState)
  }

  return { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose }
}

const RebateSettingsTable = () => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const rebateSettingsFetch = useRebateSettingsFetch()
  const { gridState, gridStatusState, tableData, setTableData, rebateSettings, tickmillCompanies } =
    rebateSettingsFetch

  const { rebateSettingsModal, handleRebateSettingsModalOpen, handleRebateSettingsModalClose } =
    useRebateSettingsModal()
  const { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose } =
    useConfirmationModal()

  const handlers = useRebateSettingsHandlers(rebateSettingsFetch)
  const mutate = useRebateSettingsMutate()
  const { handleSortChange, handleGridPageChange, handlePageSizeChange, handleSkipCountChange } =
    handlers

  const [isEdit, setIsEdit] = React.useState(false)

  const handleSubmit = async (id: string, data: CreateRebateSettings) => {
    if (isEdit) {
      await mutate.handleEdit(id, data)
    } else {
      await mutate.handleAdd(data)
    }

    await rebateSettingsFetch.fetchRebateSettingsFetch()
    setIsEdit(false)
    handleRebateSettingsModalClose()
  }

  const handleRemoveRebateSettings = async (data?: RebateSettingsDto) => {
    if (data?.id) {
      await mutate.handleRemove(data)
      await rebateSettingsFetch.fetchRebateSettingsFetch()
      handleConfirmationModalClose()
    }
  }

  const handleClose = () => {
    handleRebateSettingsModalClose()
    setIsEdit(false)
  }

  const isEditRebatePoliciesAllowed = hasPermission(
    permissions,
    PermissionsEnum.CanEditRebatePolicies
  )

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <section>
      {confirmationModal.data && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={() => handleRemoveRebateSettings(confirmationModal.data)}
          onCancel={handleConfirmationModalClose}
          visible={confirmationModal.visible}
          renderTitle={
            <div>
              <p>Are you sure you want to delete this IB rebate settings?</p>
            </div>
          }
        />
      )}
      {rebateSettingsModal.visible && (
        <RebateSettingsForm
          key={rebateSettingsModal.data?.id ?? 'new'}
          isOpen={rebateSettingsModal.visible}
          onClose={handleClose}
          data={rebateSettingsModal.data}
          tickmillCompanies={tickmillCompanies}
          isEdit={isEdit}
          onSubmit={(data) =>
            handleSubmit(rebateSettingsModal.data?.id as string, data as CreateRebateSettings)
          }
        />
      )}
      <AddButton
        text='Create Rebate Settings'
        onClick={handleRebateSettingsModalOpen}
        disabled={!isEditRebatePoliciesAllowed}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={rebateSettings}
          sort={gridState.sort}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          onSortChange={handleSortChange}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          {rebateSettingsColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <GridColumn key={idx + 2} {...column} />
            ))}
          <GridColumn
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Delete',
                      onClick: () => handleConfirmationModalOpen(gridCellProps.dataItem),
                      disabled: !isEditRebatePoliciesAllowed,
                    },
                    {
                      name: 'Edit',
                      onClick: () => {
                        setIsEdit(true)
                        handleRebateSettingsModalOpen(gridCellProps.dataItem)
                      },
                      disabled: !isEditRebatePoliciesAllowed,
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </TableStateContext.Provider>
    </section>
  )
}

export default RebateSettingsTable
